import {
  accountStore,
  IKybInfo,
  IKycInfo,
  KYB_Step,
  KYC_Step,
} from "entities/account";
import { fbLogEventOnce, FB_EVENTS } from "utils/fbEvents";
import { sessionStore } from "entities/session/model/sessionModel";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { EntityType } from "static/types";
import { useStore } from "zustand";
import useRecaptchaV3 from "./useRecaptchaV3";

type SupportedKycSteps =
  | KYC_Step.None
  | KYC_Step.Residence
  | KYC_Step.Name
  | KYC_Step.DateOfBirth
  | KYC_Step.Address
  | KYC_Step.EmailVerification
  | KYC_Step.PhoneVerification
  | KYC_Step.Documents
  // | KYC_Step.Selfie
  // | KYC_Step.ProofOfAddress
  | KYB_Step.Documents;

interface IStepsDto {
  [KYC_Step.None]: {};
  [KYC_Step.Residence]: {
    residence: string;
    nationality: string;
    place_of_birth: string;
  };
  [KYC_Step.Name]: {
    first_name: string;
    last_name: string;
    // not good solution
    company_name: string;
  };
  [KYC_Step.DateOfBirth]: {
    birth_date: string;
  };
  [KYC_Step.Address]: {
    city: string;
    street: string;
    building: string;
    apartment: string;
    zip_code: string;
  };
  [KYC_Step.EmailVerification]: {
    email: string;
    code: string;
  };
  [KYC_Step.PhoneVerification]: {
    phone: string;
    code: string;
  };
  // [KYC_Step.DocumentNumber]: {
  //   document_number: string;
  //   is_political_figure: boolean;
  // };
  [KYC_Step.Documents]: {
    only_armenia_resident?: boolean | null;
  };
  // [KYC_Step.Selfie]: {};
  // [KYC_Step.ProofOfAddress]: {};
  [KYB_Step.Documents]: {
    company_address: string;
  };
  // [KYB_Step.Name]: {
  //   company_name: string;
  // };
}

type IKycStepParams<T extends SupportedKycSteps> = {
  navigateOnSuccess: string;
  step: T;
  onSuccess?: () => void | Promise<void>;
  onError?: any;
  navigateParams?: any;
};

type IUseKycStep<T extends SupportedKycSteps> = {
  kyc: IKycInfo | null;
  kyb: IKybInfo | null;
  isLoading: boolean;
  onSubmit: (dto: IStepsDto[T], customNavigate?: string) => Promise<void>;
};

export function useVerificationStep<T extends SupportedKycSteps>(
  params: IKycStepParams<T>
): IUseKycStep<T> {
  const executeRecaptcha = useRecaptchaV3(
    process.env.REACT_APP_RECAPTCHA_SITE_KEY as string
  );
  const { t: err } = useTranslation("errors");
  const [isLoading, setIsLoading] = useState(false);
  const { entity, setReachedLimitModal } = useStore(sessionStore);
  const { kyc, kyb, startKyc, startKyb, stepKyc, stepKyb, synckyc, synckyb } =
    useStore(accountStore);

  const navigate = useNavigate();

  const onSubmit = async (dto: IStepsDto[T], customNavigate?: string) => {
    try {
      setIsLoading(true);
      let resp;
      let recaptchaToken = null;
      if (
        [KYC_Step.PhoneVerification, KYB_Step.EmailVerification].includes(
          params.step
        )
      ) {
        recaptchaToken = await executeRecaptcha("login");
      }
      if (entity === EntityType.Individual) {
        resp =
          params.step === KYC_Step.None
            ? await startKyc({})
            : await stepKyc({
                recaptchaToken,
                dto: {
                  step: params.step,
                  ...dto,
                },
              });
      } else {
        resp =
          params.step === KYC_Step.None
            ? await startKyb({})
            : await stepKyb({
                recaptchaToken,
                dto: {
                  step: params.step,
                  ...dto,
                },
              });
      }
      if (resp?.error?.code) {
        if (params?.onError) params.onError();
        else toast.error(err(String(resp.error.code)));
      } else if (resp.data) {
        const { kyc, user } = resp.data.data;
        sessionStore.setState({ user });
        if (entity === EntityType.Individual) {
          synckyc(kyc);
        } else {
          synckyb(kyc);
        }
        const userId = user?.id;
        if (params.step === KYC_Step.None) {
          void fbLogEventOnce(`kyc_started_${userId}`, FB_EVENTS.kycStarted);
        }
        if (
          params.step === KYC_Step.EmailVerification &&
          "code" in dto &&
          String((dto as { code?: string }).code || "").length === 4
        ) {
          void fbLogEventOnce(
            `email_verified_${userId}`,
            FB_EVENTS.emailVerified,
          );
        }

        if (params?.onSuccess) {
          await Promise.resolve(params.onSuccess());
        }
        if (customNavigate) {
          navigate(customNavigate);
        } else if (params.navigateOnSuccess)
          navigate(params.navigateOnSuccess, params.navigateParams);
      } else if (resp) {
        const { status } = resp;
        if (status === 429) {
          setReachedLimitModal(true);
        }
      }
    } catch (error) {
      console.log(error);
      toast.error("Server Error");
    } finally {
      setIsLoading(false);
    }
  };

  return {
    kyc,
    kyb,
    isLoading,
    onSubmit,
  };
}
